import { useState } from "react";
import { TARGET_DEPARTMENT_DAYS, TARGET_MARKDOWN_STEPS, decodeTargetClearance, parsePrice } from "../../utils/toolCalculators";
import { toolInputClass, toolLabelClass } from "./toolUi";

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function TargetClearanceTool() {
  const [original, setOriginal] = useState("");
  const [percent, setPercent] = useState<number>(30);
  const [sticker, setSticker] = useState("");
  const [department, setDepartment] = useState("");
  const parsedOriginal = parsePrice(original);
  const parsedSticker = parsePrice(sticker);
  const result = parsedOriginal && parsedOriginal > 0 ? decodeTargetClearance(parsedOriginal, percent, parsedSticker) : null;
  const day = TARGET_DEPARTMENT_DAYS.find((d) => d.department === department)?.day;

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label>
          <span className={toolLabelClass}>Original price</span>
          <input className={toolInputClass} inputMode="decimal" placeholder="e.g. 39.99" value={original} onChange={(e) => setOriginal(e.target.value)} />
        </label>
        <div>
          <span className={toolLabelClass}>Percent on the sticker</span>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Percent off">
            {TARGET_MARKDOWN_STEPS.map((p) => (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={percent === p}
                onClick={() => setPercent(p)}
                className={`rounded-xl border px-3.5 py-2.5 font-bold ${percent === p ? "border-red-600 bg-red-600 text-white" : "border-slate-300 bg-white hover:border-red-400"}`}
              >
                {p}%
              </button>
            ))}
          </div>
        </div>
        <label>
          <span className={toolLabelClass}>Price printed on the sticker (optional)</span>
          <input className={toolInputClass} inputMode="decimal" placeholder="e.g. 27.94" value={sticker} onChange={(e) => setSticker(e.target.value)} />
        </label>
        <label>
          <span className={toolLabelClass}>Department (optional)</span>
          <select className={toolInputClass} value={department} onChange={(e) => setDepartment(e.target.value)}>
            <option value="">Choose a department</option>
            {TARGET_DEPARTMENT_DAYS.map((d) => (
              <option key={d.department} value={d.department}>{d.department}</option>
            ))}
          </select>
        </label>
      </div>

      {result && (
        <div className="mt-5 space-y-4" role="status">
          <div className="rounded-xl border border-red-200 bg-white p-4 sm:p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-red-700">You pay now</p>
            <p className="mt-1 text-3xl font-extrabold">{money(result.currentPrice)}</p>
            <p className="mt-1 text-slate-600">You save {money(result.savings)} off {money(parsedOriginal!)}.</p>
            {result.endingSignal === "final" && (
              <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">This price ends in 4, which shoppers commonly report as Target's final markdown. It probably won't go lower.</p>
            )}
            {result.endingSignal === "may-drop" && (
              <p className="mt-3 rounded-lg bg-sky-50 p-3 text-sm text-sky-900">This price doesn't end in 4, so it may still drop to the next markdown.</p>
            )}
          </div>
          {result.nextSteps.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-100 text-slate-600">
                  <tr><th className="px-4 py-2">Next markdown</th><th className="px-4 py-2">Price</th><th className="px-4 py-2">Extra savings</th></tr>
                </thead>
                <tbody>
                  {result.nextSteps.map((s) => (
                    <tr key={s.percent} className="border-t border-slate-100">
                      <td className="px-4 py-2 font-semibold">{s.percent}% off</td>
                      <td className="px-4 py-2">{money(s.price)}</td>
                      <td className="px-4 py-2 text-emerald-700">+{money(s.extraSavings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {day && (
            <p className="rounded-xl border border-slate-200 bg-white p-4 text-slate-700">
              <strong>{department}</strong> is commonly reported to be marked down on <strong>{day}</strong>. Stores can differ.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
